import React, { useEffect, useEffectEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'usehooks-ts';

import { useSnackbar } from '@cms/providers/SnackbarProvider';
import { SystemThemeProvider } from '@cms/providers/ThemeProvider';
import { BROWSER_EVENTS } from '@cms/utils/constants';
import { getRouteType, Routes } from '@cms/utils/routes';
import { socket } from '@cms/utils/socket';
import { useAppSelector } from '@cms/utils/store/hooks';
import { RenderModeType } from '@cms/utils/types';
import { checkRouteRequiredRole } from '@dl/declarations';

interface Props extends React.PropsWithChildren {
    isDarkMode?: boolean;
}

const LayoutWrapperContent: React.FC<Props> = ({ children }) => {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const [searchParams] = useSearchParams();
    const { pathname, search, hash } = useLocation();
    const isDesktop = useMediaQuery('(min-width: 1024px)')
    const { isAuthenticated, profile } = useAppSelector((state) => state.user)

    const onHandleUnAuthenticated = useEffectEvent(() => {
        const routeType = getRouteType(pathname);

        if (routeType === "app") {
            navigate({
                pathname: Routes.SIGN_IN,
                search: `?redirect_pathname=${pathname}&redirect_search=${search.slice(1)}&redirect_hash=${hash}`,
            })
        }
    })

    const onHandleCheckRouteRequiredRole = useEffectEvent(() => {
        /** /app/<resource> */
        const usedPathname = pathname.split('/').pop() || ''
        const mode = searchParams.get('mode') as RenderModeType;

        switch (mode) {
            case 'create': {
                const hasAccess = checkRouteRequiredRole(usedPathname, 'c', profile.permissions)
                if (!hasAccess) {
                    navigate(Routes.OVERVIEW);
                }
                break;
            }
            case 'update': {
                const hasAccess = checkRouteRequiredRole(usedPathname, 'u', profile.permissions)
                if (!hasAccess) {
                    navigate(Routes.OVERVIEW);
                }
                break;
            }

            default:
                break;
        }
    })

    useEffect(() => {
        if (isAuthenticated) {
            if (!profile.isPasswordSet) {
                showSnackbar("Please set your password to continue", "warning");
                navigate({
                    pathname: Routes.SET_PASSWORD
                });
            }
            else if (!profile.isTwoFactorSecretSetup) {
                showSnackbar("Please setup your two-factor authentication to continue", "warning");
                navigate({
                    pathname: Routes.TWO_FA
                });
            }
            else if (!profile.isTwoFactorSecretVerified) {
                showSnackbar("Please verify your two-factor authentication to continue", "warning");
                navigate({
                    pathname: Routes.TWO_FA,
                });
            }
            else {
                onHandleCheckRouteRequiredRole()
            }
        }
        else {
            onHandleUnAuthenticated()
        }
    }, [isAuthenticated, profile])

    /** 
     * Connect to the socket if the user is authenticated
     * otherwise disconnect from the socket
    */
    useEffect(() => {
        if (isAuthenticated) {
            socket.connect()
        }
        else {
            socket.disconnect()
        }
    }, [isAuthenticated])

    useEffect(() => {
        const handler = () => {
            onHandleUnAuthenticated()
        }

        window.addEventListener(BROWSER_EVENTS.UNAUTHORIZED, handler);

        return () => window.removeEventListener(BROWSER_EVENTS.UNAUTHORIZED, handler);
    }, []);

    if (!isDesktop) {
        return (
            <div className='flex flex-col items-center justify-center'>
                <p>For best results, please use a desktop browser.</p>
            </div>
        )
    }

    return children
}

export const LayoutWrapper: React.FC<Props> = ({ isDarkMode = false, children }) => {
    return (
        <SystemThemeProvider isDarkMode={isDarkMode}>
            <LayoutWrapperContent>
                {children}
            </LayoutWrapperContent>
        </SystemThemeProvider>
    )
}